import type {
  BlotterFilterValues,
  IBlotterFilteringApi,
  IBlotterRequest,
  ISearchResponse,
  ISort,
  ServerSideBlotterSearchRequest,
} from 'apiConnector/blotters/common';

export type { ServerSideBlotterSearchRequest } from 'apiConnector/blotters/common';

import {
  BlotterRequestByBlotterType,
  FetchColumnDefinitionEpicApi,
  FetchItemsEpicApi,
} from 'epic/blotters/tradesBlotter/tradesBlottersEpic';
import type { StringFileResponse } from 'models/FileResponse';
import { type Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import type { ActivityFilter, IBlotterColumnDefinition, Sort } from 'state/blotters/common/blotter.model';
import { DlpProductTradeItem, TradesBlottersItemByBlotterType } from 'state/blotters/trades/types/blotterItem';
import { BlotterType } from 'state/blotters/trades/types/types';
import type { HttpError, IHttpClient, IHttpConfig } from 'utils/http/httpClient.model';

export type TradesBlottersRequestParams = {
  page: number;
  itemsPerPage: number;
  sort?: readonly Sort[];
};

export interface ITradesBlotterRequest extends IBlotterRequest {
  activityScope: ActivityFilter;
  currency?: string;
  endDate: string;
  filters?: any;
  page: number;
  platforms?: string;
  product?: string;
  size: number;
  startDate: string;
  status?: string;
}

export interface IFetchCreditCheckEpicApi {
  fetchCreditCheckStatus(): Observable<{ numberOfKO: number }>;
}

export interface ITradesBlotterFetchCsvApi {
  dlpFetchCSV<T extends BlotterType>(request: BlotterRequestByBlotterType<T>): Observable<StringFileResponse>;

  fetchCSV<T extends BlotterType>(
    request: ServerSideBlotterSearchRequest,
    blotterTypeFilter: T,
  ): Observable<StringFileResponse>;
}

export interface ITradesBlotterApi
  extends
    IFetchCreditCheckEpicApi,
    ITradesBlotterFetchCsvApi,
    FetchColumnDefinitionEpicApi,
    FetchItemsEpicApi,
    IBlotterFilteringApi {
  searchDlpProductTrades(isin: string): Observable<ISearchResponse<DlpProductTradeItem>>;
  serverSideSearchTrades(
    request: ServerSideBlotterSearchRequest,
  ): Observable<ISearchResponse<TradesBlottersItemByBlotterType<'trades'>>>;
}

// See https://developer.fr.world.socgen/rtd/apis/reference/guidelines/technical.html#rule-api-298
function mapSortQueryParameter(sorts?: readonly ISort[]) {
  return (sorts ?? []).map(s => (s.direction?.toLowerCase() === 'desc' ? 'desc_' : 'asc_') + s.column).join(',');
}

function buildQueryParameters(request: BlotterRequestByBlotterType<'dlpProducts'>): IHttpConfig['params'] {
  const sort = mapSortQueryParameter(request.sort);
  const params: IHttpConfig['params'] = {
    pageSize: request.size,
    page: request.page,
    activityScope: request.activityScope,
    startDate: request.startDate,
    endDate: request.endDate,
  };

  if (request.fullTextSearch) {
    params.search = request.fullTextSearch;
  }
  if (sort) {
    params.sort = sort;
  }

  return params;
}

export function tradesBlotterApiConnector(http: IHttpClient): ITradesBlotterApi {
  return {
    searchDlpProductTrades(isin: string): Observable<ISearchResponse<DlpProductTradeItem>> {
      return http
        .getJSON<ISearchResponse<DlpProductTradeItem>>({
          url: `api/v1/blotter/dlp-products/${isin}/trades`,
          tryParseErrorPayload: true,
          parseErrorPayload: true,
          headers: { Accept: 'application/json' },
        })
        .pipe(map(({ payload }) => payload));
    },
    serverSideSearchTrades(request: ServerSideBlotterSearchRequest) {
      return http
        .postJSON<ISearchResponse<TradesBlottersItemByBlotterType<'trades'>>>({
          url: 'api/v4/blotter/trades',
          body: request,
          headers: { Accept: 'application/json' },
        })
        .pipe(map(({ payload }) => payload));
    },
    fetchFilterValues(field: string) {
      return http
        .getJSON<BlotterFilterValues>({
          url: `api/v4/blotter/trades/filterValues?field=${field}`,
        })
        .pipe(map(({ payload }) => payload));
    },
    search<T extends BlotterType>(request: BlotterRequestByBlotterType<T>, blotter: BlotterType) {
      let url;
      if (blotter === 'dlpProducts') {
        return http
          .getJSON<ISearchResponse<TradesBlottersItemByBlotterType<T>>>({
            url: 'api/v1/blotter/dlp-products',
            params: buildQueryParameters(request),
            headers: { Accept: 'application/json' },
          })
          .pipe(map(({ payload }) => payload));
      } else {
        url = `api/v2/blotter/${blotter}`;
        return http
          .postJSON<ISearchResponse<TradesBlottersItemByBlotterType<T>>>({
            url,
            body: request,
            headers: { Accept: 'application/json' },
          })
          .pipe(map(({ payload }) => payload));
      }
    },

    fetchColumnDefinition<T extends BlotterType>(blotterType: T) {
      return http
        .getJSON<{
          data: {
            columnDefinitions: IBlotterColumnDefinition<TradesBlottersItemByBlotterType<T>>[];
          };
          isSuccess: boolean;
        }>({
          url: `api/v1/blotter/column-definitions/${blotterType}`,
        })
        .pipe(map(({ payload }) => payload.data?.columnDefinitions ?? []));
    },
    dlpFetchCSV<T extends BlotterType>(request: BlotterRequestByBlotterType<T>) {
      return http
        .getRAW({
          url: 'api/v1/blotter/dlp-products/csv',
          params: buildQueryParameters(request),
          headers: { Accept: 'text/csv' },
        })
        .pipe(
          map(response => {
            return {
              data: response.payload,
              filename: 'flow_products_blotter.csv',
            };
          }),
        );
    },

    fetchCSV<T extends BlotterType>(request: ServerSideBlotterSearchRequest, blotterTypeFilter: T) {
      return http
        .postRAW({
          url: `api/v4/blotter/${blotterTypeFilter}/export`,
          body: request,
          headers: { Accept: 'text/csv' },
          parseErrorPayload: true,
          timeOut: 600_000, // OS/Browser system timeout is likely to be shorter
        })
        .pipe(
          map(response => ({
            data: response.payload,
            filename: response.attachmentName,
          })),
          catchError((err: HttpError) => (err.status === 400 ? throwError(() => err.payload) : throwError(() => err))),
        );
    },
    fetchCreditCheckStatus() {
      return http
        .getJSON<{ numberOfKO: number }>({
          url: `api/v1/blotter/trades/credit-check-status`,
        })
        .pipe(map(({ payload }) => payload));
    },
  };
}
